import { Component, Input, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { CacheService } from 'projects/common/src/app/services';
import { ExcelExportService } from 'projects/common/src/app/services/utils/excel-export.service';
import { Subscription } from 'rxjs';
import { MtTable } from 'src/app/modules/core/classes/mt-table';
import { IPagination } from 'src/app/modules/core/components/mt-table-pagination/mt-table-pagination.component';
import { NgbdSortableHeaderDirective, SortEvent } from 'src/app/modules/core/directives/ngbdSortableHeader.directive';
import { environment } from 'src/environments/environment';
import { IChecklistExcel } from '../../../interfaces/checklistExcel';
import { IReportChecklist } from '../../../interfaces/report-checklist';
import { ReportChecklistService } from '../../../services/report-checklist.service';
import { ChecklistModalComponent } from '../checklist-modal/checklist-modal.component';

@Component({
  selector: 'app-checklist-table',
  templateUrl: './checklist-table.component.html',
  styleUrls: ['./checklist-table.component.scss'],
})
export class ChecklistTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public data: IReportChecklist[];

  @ViewChildren(NgbdSortableHeaderDirective)
  public headers: QueryList<NgbdSortableHeaderDirective>;

  private subSource: Subscription;
  private subCollectionSize: Subscription;

  public viewData: any;
  public table: MtTable;
  public pageSize: number;
  public collectionSize: number;
  public btnSpinner: boolean;
  public disabledExcelBtn: boolean;

  constructor(
    private modal: NgbModal,
    private cacheService: CacheService,
    private reportChecklistService: ReportChecklistService,
    private _excelExportService: ExcelExportService,
  ) {
    this.viewData = [];
    this.pageSize = 10;
    this.subCollectionSize = new Subscription();
    this.table = new MtTable(true, this.pageSize, ['createAt']);
    this.disabledExcelBtn = false;
  }

  ngOnInit() {
    // Recive the data processed by the MtTable class
    this.subSource = this.table.source$.subscribe((data: any[]) => {
      data.forEach((element) => {
        element.spinner = false;
        element.disabledExcelBtn = false;
      });
      this.viewData = data;
    });

    // Listening if the length of the collection changes when filtering
    this.subCollectionSize = this.table.collectionSize$.subscribe((size) => {
      this.collectionSize = size;
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && changes.data.currentValue) {
      this.onChangePage(1); // back to first page
      this.table.init(changes.data.currentValue);
    }
  }

  ngOnDestroy(): void {
    if (this.subSource) {
      this.subSource.unsubscribe();
    }
    if (this.subCollectionSize) {
      this.subCollectionSize.unsubscribe();
    }
  }

  /**
   * Open Modal
   *
   * @param value machine value
   */
  public openModal(value): void {
    const modalRef = this.modal.open(ChecklistModalComponent, {
      centered: true,
      windowClass: 'checklist-modal',
      backdrop: 'static',
    });

    modalRef.componentInstance.checkList = value;
  }

  /**
   * Sort table
   *
   * @param param0 object with column and direction to sort
   */
  public onSort({ column, direction }: SortEvent): void {
    // resetting other headers
    this.headers.forEach((header) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });

    this.table.sort(column, direction);
  }

  /**
   * Change page size of the table
   *
   * @param value page size
   */
  public onChangePageSize(value: IPagination): void {
    this.table.changePageSize(value);
  }

  /**
   * Change the page of the table
   *
   * @param value page number
   */
  public onChangePage(value: number): void {
    this.table.changePage(value);
  }

  /**
   * Execute excel download
   */
  public downloadExcel(value: IReportChecklist): void {
    const format = `${environment.dateFormatSystem} ${environment.timeFormat}`;
    const date = moment(value.createAt, format).format(`${environment.dateFormatSystem}`);
    const params: IChecklistExcel = {
      checklistDate: value.createAt,
      sessionId: value.sessionId,
      operatorid: value.operatorid,
      machineId: value.machineid,
      checklistId: value.checklistId,
      companyId: this.cacheService.local('session').user.company.id,
    };

    value.spinner = true;
    value.disabledExcelBtn = true;
    this.reportChecklistService.GetChecklistReportDetailExcel(params).subscribe({
      next: (response) => {
        this._excelExportService.exportAsExcel(response, 'report-checklist', null, date, false);
        value.spinner = false;
        value.disabledExcelBtn = false;
        this._excelExportService.messageToastSuccess();
      },
      error: (error) => {
        console.error(error);
        value.spinner = false;
        value.disabledExcelBtn = false;
        this._excelExportService.messageToastError();
      },
    });
  }
}
